import { apcaLc, Rgb } from './apcaContrast'

import Colors from '~/client/src/shared/theme.module.scss'

// Which ink a label wears, decided from the SCENE rather than from the rendered picture.
//
// Sampling the composited frame to choose a colour that is then composited into it is
// self-reference: it oscillated on a still camera, it mostly measured the label's own casing,
// and it called a black markup light. So the ground is composited here from what we drew — the
// basemap by identity, plus the markup fills containing the label — which cannot flicker,
// because nothing it reads depends on what it writes.
//
// It deliberately cannot see satellite imagery detail; that falls back to the basemap, and the
// casing carries it. Restoring imagery adaptation means reading the raster TILE, never the
// canvas.
export type SurfaceTone = 'light' | 'dark'

export interface IGroundFill {
  ring: Array<[number, number]>
  // Ground inside the ring that the fill does not cover.
  holes?: Array<Array<[number, number]>>
  colorHex: string
  opacity: number
}

// One raster's contribution at one point — already resolved spatially by whoever read it, so
// this module never learns what a tile or a plan image is. Alpha carries coverage: a remote
// tileset has no bounds anywhere, so transparency is the only in-band signal for "no imagery
// here", and ignoring it would paint every off-footprint label as if it stood on black.
export interface IGroundRaster {
  rgb: [number, number, number]
  alpha: number
  // Spread of LINEAR luminance across the sampled pixels. The mean says what the patch averages
  // to; this says whether that mean describes anything — drafting linework on white paper and
  // open dirt average to the same grey, and only one of them is usable ground.
  contrast?: number
}

export interface IGroundContext {
  // The basemap's representative ground, used where nothing covers the label and as the
  // backdrop the layers above composite over.
  basemap: SurfaceTone
  // Rasters the label stands on — plan sheets, tilesets, imagery — bottom-up. Absent until
  // something reads them; the basemap constant answers alone, which is what shipped before
  // and what any unreadable raster degrades back to.
  rasters?: IGroundRaster[]
  // Painter's order: later entries draw on top. Above the rasters — map-file overlays are
  // held below the shape layers, and markups above them (see enforceShapeLayerOrder).
  fills: IGroundFill[]
}

// Stand-ins for the two basemaps. Only ever consulted through a translucent fill or where
// no fill applies, so their exact value moves nothing that a light/dark decision can see —
// what matters is that one is clearly light and the other clearly dark.
const BASEMAP_GROUND: Record<SurfaceTone, string> = {
  light: '#e8e6e1',
  dark: '#3d3a35',
}

function channels(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// Exported so the raster sampler averages in the same space this module reasons in, rather
// than growing a second copy that can drift.
export function linearize(channel: number): number {
  const c = channel / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

export function delinearize(linear: number): number {
  const c = Math.min(1, Math.max(0, linear))
  const s = c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055
  return s * 255
}

const INK_ON_LIGHT: Rgb = channels(Colors.mapLabelOnLight)
const INK_ON_DARK: Rgb = channels(Colors.mapLabelOnDark)

// Alpha compositing happens on gamma-encoded channels, which is where the compositor does
// it — blending luminances instead would put 50% black over white at L 0.5 rather than the
// 0.216 it actually renders, straddling the crossover in the wrong direction.
function over(
  base: [number, number, number],
  fill: [number, number, number],
  alpha: number,
): [number, number, number] {
  const a = Math.min(1, Math.max(0, alpha))
  return [
    base[0] * (1 - a) + fill[0] * a,
    base[1] * (1 - a) + fill[1] * a,
    base[2] * (1 - a) + fill[2] * a,
  ]
}

// Ray casting. turf could do this, but the ring is already in hand and a polygon test on a
// handful of markups per label does not need a geometry library dragged into the path.
export function isPointInRing(
  lng: number,
  lat: number,
  ring: Array<[number, number]>,
): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const straddles = yi > lat !== yj > lat
    if (straddles && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

// The colour a label actually stands on, composited in paint order. Kept as a colour rather
// than reduced to a luminance because the contrast model reads all three channels.
export function groundColourAt(
  lng: number,
  lat: number,
  ctx: IGroundContext,
): Rgb {
  let rgb = channels(BASEMAP_GROUND[ctx.basemap])
  for (const raster of ctx.rasters ?? []) {
    rgb = over(rgb, raster.rgb, raster.alpha)
  }
  for (const fill of ctx.fills) {
    if (
      isPointInRing(lng, lat, fill.ring) &&
      !fill.holes?.some(hole => isPointInRing(lng, lat, hole))
    ) {
      rgb = over(rgb, channels(fill.colorHex), fill.opacity)
    }
  }
  return rgb
}

// Whichever ink reads better against that ground. No threshold: the crossover falls out of the
// ink pair under the contrast model rather than being a number someone chose.
//
// Ink against GROUND, not against its own casing. Casing-aware scoring hands dark ink nearly
// every ground, because the casing pair is asymmetric — the casing's job is a contrast floor,
// not choosing the ink. One cell's answer only; stickiness is the caller's vote margin.
export function toneForColour(ground: Rgb): SurfaceTone {
  return Math.abs(apcaLc(INK_ON_LIGHT, ground)) >=
    Math.abs(apcaLc(INK_ON_DARK, ground))
    ? 'light'
    : 'dark'
}

// What a re-resolution has to notice about the ground. GEOMETRY, not just vertex count: dragging
// a shape point moves the ring while leaving colour, opacity, count and the item's own
// coordinates untouched, so nothing else the tone hook watches would change and the label would
// keep ink chosen for ground the fill no longer covers.
export function groundSignature(ground: IGroundContext): string {
  const fills = (ground.fills ?? []).map(fill => {
    let fold = fill.ring.length
    for (const [lng, lat] of [fill.ring, ...(fill.holes ?? [])].flat()) {
      fold = (fold * 31 + Math.round(lng * 1e5)) | 0
      fold = (fold * 31 + Math.round(lat * 1e5)) | 0
    }
    return `${fill.colorHex}:${fill.opacity}:${fold}`
  })
  return `${ground.basemap}|${fills.join(',')}`
}
